"use client";

import Link from "next/link";
import { Suspense, useCallback, useEffect, useState, useSyncExternalStore } from "react";
import { CampaignAttributionCapture } from "./CampaignAttributionCapture";
import {
  ANALYTICS_CONSENT_CHANGED_EVENT,
  ANALYTICS_CONSENT_SETTINGS_EVENT,
  readAnalyticsConsent,
  writeAnalyticsConsent,
  type AnalyticsConsent,
} from "@/lib/analytics-consent";
import { clearPendingAnalyticsOperations } from "@/lib/analytics";
import { canUseGoogleMeasurement, isNativeApp, subscribeToNativePlatform } from "@/lib/native/platform";
import { GoogleAdsScript } from "./GoogleAdsScript";
import { PostHogScript } from "./PostHogScript";

function subscribeToAnalyticsConsent(onStoreChange: () => void) {
  window.addEventListener(ANALYTICS_CONSENT_CHANGED_EVENT, onStoreChange);
  return () => {
    window.removeEventListener(ANALYTICS_CONSENT_CHANGED_EVENT, onStoreChange);
  };
}

export function AnalyticsConsentManager() {
  const consent = useSyncExternalStore<
    AnalyticsConsent | null | undefined
  >(
    subscribeToAnalyticsConsent,
    readAnalyticsConsent,
    () => undefined,
  );
  const [settingsOpen, setSettingsOpen] = useState(false);
  const native = useSyncExternalStore(subscribeToNativePlatform, isNativeApp, () => false);

  useEffect(() => {
    function openSettings() {
      setSettingsOpen(true);
    }

    window.addEventListener(ANALYTICS_CONSENT_SETTINGS_EVENT, openSettings);
    return () => {
      window.removeEventListener(ANALYTICS_CONSENT_SETTINGS_EVENT, openSettings);
    };
  }, []);

  const chooseConsent = useCallback((choice: AnalyticsConsent) => {
    writeAnalyticsConsent(choice);

    if (choice === "accepted") {
      window.posthog?.opt_in_capturing?.();
      if (canUseGoogleMeasurement()) window.gtag?.("consent", "update", {
        analytics_storage: "granted",
        ad_storage: "granted",
        ad_user_data: "granted",
        ad_personalization: "granted",
      });
    } else {
      clearPendingAnalyticsOperations();
      window.posthog?.stopSessionRecording?.();
      window.posthog?.reset?.();
      window.posthog?.opt_out_capturing?.({ clear_persistence: true });
      if (canUseGoogleMeasurement()) window.gtag?.("consent", "update", {
        analytics_storage: "denied",
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
      });
    }

    setSettingsOpen(false);
  }, []);

  const canClose = consent !== null;
  const showDialog = consent !== undefined && (consent === null || settingsOpen);

  return (
    <>
      <Suspense fallback={null}><CampaignAttributionCapture /></Suspense>
      {consent === "accepted" ? (
        <>
          <PostHogScript />
          {!native ? <GoogleAdsScript /> : null}
        </>
      ) : null}
      {showDialog ? (
        <AnalyticsConsentDialog
          canClose={canClose}
          native={native}
          onAccept={() => chooseConsent("accepted")}
          onReject={() => chooseConsent("rejected")}
          onClose={() => setSettingsOpen(false)}
        />
      ) : null}
    </>
  );
}

export function AnalyticsConsentDialog({
  canClose,
  native = false,
  onAccept,
  onReject,
  onClose,
}: {
  canClose: boolean;
  native?: boolean;
  onAccept: () => void;
  onReject: () => void;
  onClose: () => void;
}) {
  return (
    <section
      aria-labelledby="analytics-consent-title"
      aria-modal="false"
      data-testid="analytics-consent-dialog"
      role="dialog"
      className="fixed inset-x-3 bottom-3 z-[1200] mx-auto max-w-3xl rounded-lg border border-border-light bg-white p-4 shadow-[0_12px_40px_rgba(27,42,74,0.24)] sm:inset-x-6 sm:bottom-6 sm:p-5"
    >
      <h2
        id="analytics-consent-title"
        className={`font-serif text-xl font-bold text-navy ${canClose ? "pr-11" : ""}`}
      >
        Optional analytics cookies
      </h2>
      <p className="mt-1.5 text-sm leading-6 text-text-muted">
        {native
          ? "We use PostHog to understand how PeakWave is used and fix problems. We only load it if you accept. Google advertising and measurement are disabled in the app."
          : "We use PostHog and Google measurement to understand how PeakWave is used and fix problems. We only load them if you accept."}{" "}
        You can change this choice at any time in Cookie settings.{" "}
        <Link
          href="/privacy"
          className="font-semibold text-teal-dark underline underline-offset-2"
        >
          Read our privacy policy
        </Link>
        .
      </p>
      {canClose ? (
        <button
          type="button"
          aria-label="Close cookie settings"
          onClick={onClose}
          className="absolute right-3 top-2 flex h-11 w-11 items-center justify-center rounded-sm text-2xl leading-none text-text-muted transition-colors hover:bg-background hover:text-navy focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy sm:right-4 sm:top-3"
        >
          <span aria-hidden="true">&times;</span>
        </button>
      ) : null}
      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        <button
          type="button"
          onClick={onReject}
          className="min-h-11 rounded-sm bg-navy px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-teal-dark focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy"
        >
          Reject analytics
        </button>
        <button
          type="button"
          onClick={onAccept}
          className="min-h-11 rounded-sm bg-navy px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-teal-dark focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy"
        >
          Accept analytics
        </button>
      </div>
    </section>
  );
}
